import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Box,
  InputAdornment,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    loginId: "",
    password: "",
    role: "",
    companyId: "",
    status: "ACTIVE",
  });
  // パスワード表示制御用の状態を管理
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [showDialogPassword, setShowDialogPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery, searchRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/users", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchQuery,
          role: searchRole,
        },
      });
      if (process.env.NODE_ENV === "development") {
        console.log("ユーザー一覧取得レスポンス:", response.data);
      }
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error("ユーザー情報の取得に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/api/companies");
      console.log("企業一覧取得レスポンス:", response.data);
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("企業情報の取得に失敗しました:", error);
      // エラーメッセージをユーザーに表示する処理を追加
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        loginId: user.loginId,
        password: "",
        role: user.role,
        companyId: user.companyId ? user.companyId._id : "",
        status: user.status,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: "",
        loginId: "",
        password: "",
        role: "",
        companyId: "",
        status: "ACTIVE",
      });
    }
    // ダイアログ入力欄のパスワード表示状態を初期化
    setShowDialogPassword(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setShowDialogPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (editingUser) {
        await api.put(`/api/users/${editingUser._id}`, formData);
      } else {
        await api.post("/api/users", formData);
      }
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      console.error("ユーザー情報の保存に失敗しました:", error);
      // エラーメッセージをユーザーに表示する処理を追加
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("このユーザーを削除してもよろしいですか？")) {
      try {
        setDeletingId(userId);
        await api.delete(`/api/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error("ユーザーの削除に失敗しました:", error);
        // エラーメッセージをユーザーに表示する処理を追加
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      SYSTEM_ADMIN: "システム管理者",
      COMPANY_ADMIN: "企業管理者",
      BRAND_ADMIN: "ブランド管理者",
      STORE_ADMIN: "店舗管理者",
    };
    return roles[role] || role;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "warning";
      case "DELETED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "有効";
      case "INACTIVE":
        return "無効";
      case "DELETED":
        return "削除済み";
      default:
        return status;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleRoleFilterChange = (event) => {
    setSearchRole(event.target.value);
    setPage(0);
  };

  const togglePasswordVisibility = async (user) => {
    const current = users.find((u) => u._id === user._id);
    const isMasked =
      !current?.plainPassword ||
      current.plainPassword === "********" ||
      current.plainPassword === "-";
    try {
      if (isMasked) {
        const resp = await api.post(`/api/users/${user._id}/reveal-password`);
        const pw = resp.data?.plainPassword || "********";
        setUsers((prev) =>
          prev.map((u) =>
            u._id === user._id ? { ...u, plainPassword: pw } : u
          )
        );
        setVisiblePasswords((prev) => ({ ...prev, [user._id]: true }));
        return;
      }
    } catch (e) {
      console.error("パスワード表示に失敗しました", e);
    }
    setVisiblePasswords((prev) => ({ ...prev, [user._id]: !prev[user._id] }));
  };

  return (
    <Container aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs>
          <Typography variant="h5" component="h1">
            ユーザー管理
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
          >
            新規ユーザー登録
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="ユーザー検索"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="名前、ログインIDで検索..."
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>権限で絞り込み</InputLabel>
              <Select
                value={searchRole}
                onChange={handleRoleFilterChange}
                label="権限で絞り込み"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="SYSTEM_ADMIN">システム管理者</MenuItem>
                <MenuItem value="COMPANY_ADMIN">企業管理者</MenuItem>
                <MenuItem value="BRAND_ADMIN">ブランド管理者</MenuItem>
                <MenuItem value="STORE_ADMIN">店舗管理者</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名前</TableCell>
              <TableCell>ログインID</TableCell>
              <TableCell>パスワード</TableCell>
              <TableCell>権限</TableCell>
              <TableCell>所属企業</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.loginId}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ mr: user.plainPassword ? 1 : 0 }}
                      >
                        {visiblePasswords[user._id]
                          ? user.plainPassword || "-"
                          : user.plainPassword
                          ? "••••••"
                          : "-"}
                      </Typography>
                      {true ? (
                        <IconButton
                          size="small"
                          onClick={() => togglePasswordVisibility(user)}
                          aria-label="パスワード表示切り替え"
                        >
                          {visiblePasswords[user._id] ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>{getRoleLabel(user.role)}</TableCell>
                  <TableCell>
                    {user.companyId ? user.companyId.name : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(user.status)}
                      color={getStatusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                      disabled={Boolean(deletingId)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user._id)}
                      disabled={deletingId === user._id}
                    >
                      {deletingId === user._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
          }
        />
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? "ユーザー情報編集" : "新規ユーザー登録"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="名前"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="loginId"
                label="ログインID"
                value={formData.loginId}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="password"
                label="パスワード"
                type={showDialogPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required={!editingUser}
                helperText={
                  editingUser ? "変更する場合のみ入力してください" : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowDialogPassword((prev) => !prev)}
                        edge="end"
                        aria-label="パスワード表示切り替え"
                      >
                        {showDialogPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>権限</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  label="権限"
                >
                  <MenuItem value="SYSTEM_ADMIN">システム管理者</MenuItem>
                  <MenuItem value="COMPANY_ADMIN">企業管理者</MenuItem>
                  <MenuItem value="BRAND_ADMIN">ブランド管理者</MenuItem>
                  <MenuItem value="STORE_ADMIN">店舗管理者</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>所属企業</InputLabel>
                <Select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  label="所属企業"
                >
                  <MenuItem value="">なし</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>ステータス</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="ステータス"
                >
                  <MenuItem value="ACTIVE">有効</MenuItem>
                  <MenuItem value="INACTIVE">無効</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users;
